import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Shield, Activity, Database, Lock, AlertTriangle } from 'lucide-react';

// --- ANIMATION VARIANTS ---
const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
};

const tableRowVariants = {
  hidden: { opacity: 0, x: -10 },
  show: { opacity: 1, x: 0 }
};

export default function SecOpsDashboard() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ logs: [], cacheHits: 0 });

  useEffect(() => {
    if (!user || !user.org_id) return;
    const fetchLogs = async () => {
      try {
        const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const res = await axios.get(`${baseURL}/api/firewall/logs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch (error) {
        console.error("Failed to load SecOps data", error);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [user, token]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] w-full">
      <motion.div 
        animate={{ scale: [1, 1.1, 1], rotate: [0, 90, 180, 270, 360] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className="w-16 h-16 bg-gradient-to-tr from-slate-800 to-slate-900 rounded-2xl flex items-center justify-center shadow-lg shadow-slate-900/30 mb-6"
      >
        <Shield className="w-8 h-8 text-white" />
      </motion.div>
      <h3 className="text-xl font-bold text-slate-800 tracking-tight">Securing Connection...</h3>
      <p className="text-slate-500 text-sm mt-2">Retrieving live telemetry from the Zero-Trust Gateway.</p>
    </div>
  );

  const piiRedactions = data.logs.filter(l => l.action_taken === 'REDACTED_PII').length;
  const blockedAttacks = data.logs.filter(l => l.action_taken === 'BLOCKED_INJECTION').length;
  const blockedAgents = data.logs.filter(l => l.action_taken === 'BLOCKED_TOOL').length;
  const estimatedSavings = (data.cacheHits * 0.015).toFixed(2);

  const getActionBadge = (action) => {
    switch(action) {
      case 'REDACTED_PII': return <span className="px-2.5 py-1 bg-amber-100 text-amber-800 rounded text-xs font-bold border border-amber-200">PII Redacted</span>;
      case 'BLOCKED_INJECTION': return <span className="px-2.5 py-1 bg-red-100 text-red-800 rounded text-xs font-bold border border-red-200">Attack Blocked</span>;
      case 'BLOCKED_TOOL': return <span className="px-2.5 py-1 bg-purple-100 text-purple-800 rounded text-xs font-bold border border-purple-200">Unauthorized Agent</span>;
      case 'PASSED': return <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 rounded text-xs font-bold border border-emerald-200">Passed</span>;
      default: return <span className="px-2.5 py-1 bg-slate-100 text-slate-800 rounded text-xs font-bold border border-slate-200">{action}</span>;
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-7xl mx-auto p-8 pb-24">
      <div className="mb-8">
        <h1 className="text-3xl font-black text-slate-900 flex items-center gap-3">
          <Shield className="w-8 h-8 text-blue-600" /> SecOps Control Center
        </h1>
        <p className="text-slate-500 mt-2 font-medium">Live telemetry for Runtime Policy Enforcement, MCP Governance, and API caching.</p>
      </div>

      {/* TOP KPI CARDS */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
        <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 border-t-4 border-t-amber-500 hover:-translate-y-1 transition-transform">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">PII Redactions</p>
              <h3 className="text-3xl font-black text-slate-900 mt-1">{piiRedactions}</h3>
            </div>
            <Lock className="w-6 h-6 text-amber-500" />
          </div>
          <p className="text-xs text-slate-500 mt-3">Data leaks prevented natively.</p>
        </motion.div>
        
        <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 border-t-4 border-t-red-500 hover:-translate-y-1 transition-transform">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Attacks Blocked</p>
              <h3 className="text-3xl font-black text-slate-900 mt-1">{blockedAttacks}</h3>
            </div>
            <AlertTriangle className="w-6 h-6 text-red-500" />
          </div>
          <p className="text-xs text-slate-500 mt-3">Prompt injections & jailbreaks stopped.</p>
        </motion.div>
        
        <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 border-t-4 border-t-purple-500 hover:-translate-y-1 transition-transform">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Rogue Agents Stopped</p>
              <h3 className="text-3xl font-black text-slate-900 mt-1">{blockedAgents}</h3>
            </div>
            <Activity className="w-6 h-6 text-purple-500" />
          </div>
          <p className="text-xs text-slate-500 mt-3">Unauthorized MCP tool executions.</p>
        </motion.div>
        
        <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 border-t-4 border-t-emerald-500 hover:-translate-y-1 transition-transform">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Cache Savings</p>
              <h3 className="text-3xl font-black text-emerald-600 mt-1">${estimatedSavings}</h3>
            </div>
            <Database className="w-6 h-6 text-emerald-500" />
          </div>
          <p className="text-xs text-slate-500 mt-3">Saved across {data.cacheHits} duplicate queries.</p>
        </motion.div>
      </motion.div>

      {/* FIREWALL LOGS TABLE */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
          <h3 className="font-bold text-slate-800">Live Interception Log</h3>
          <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-600 bg-emerald-100 px-3 py-1 rounded-full">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span> Firewall Active
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="p-4 font-bold">Timestamp</th>
                <th className="p-4 font-bold">Action Taken</th>
                <th className="p-4 font-bold">Context / Reason</th>
                <th className="p-4 font-bold">Prompt / Payload snippet</th>
              </tr>
            </thead>
            <motion.tbody variants={containerVariants} initial="hidden" animate="show" className="divide-y divide-slate-100 text-sm">
              {data.logs.length === 0 ? (
                <tr><td colSpan="4" className="p-8 text-center text-slate-500">No telemetry data recorded yet. Run tests to populate.</td></tr>
              ) : (
                data.logs.map((log) => (
                  <motion.tr variants={tableRowVariants} key={log.id} className="hover:bg-slate-50 transition-colors">
                    <td className="p-4 text-slate-500 whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td className="p-4">
                      {getActionBadge(log.action_taken)}
                    </td>
                    <td className="p-4">
                      <p className="font-medium text-slate-800">{log.flag_reason}</p>
                      {log.tool_invoked && <p className="text-xs text-slate-500 mt-0.5">Tool: <span className="font-mono">{log.tool_invoked}</span></p>}
                    </td>
                    <td className="p-4">
                      <div className="max-w-xs truncate font-mono text-xs bg-slate-100 p-2 rounded text-slate-600 border border-slate-200" title={log.original_prompt}>
                        {log.original_prompt}
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </motion.tbody>
          </table>
        </div>
      </motion.div>
    </motion.div>
  );
}